import { useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";

import useAccountMutations from "../../data/useAccountMutations";
import useAccounts from "../../data/useAccounts";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal, { ModalProps } from "../../layouts/Modal";
import { AccountCreateEditFormFields, AccountStatus, AccountType, IAccount } from "../../models/account/IAccount";
import { IContact } from "../../models/Contact";
import { ContactCombobox } from "../contacts/ContactCombobox";
import useNotification from "../notifications/useNotifications";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";
import { CheckboxInput } from "../shared/Inputs/CheckboxInput";

interface AccountCreateEditModalProps
  extends Omit<ModalProps, "children" | "width"> {
  account?: IAccount;
  newAccountName?: string;
}

export function AccountCreateEditModal({
  open,
  setOpen,
  account,
  newAccountName,
}: AccountCreateEditModalProps) {
  const { register, handleSubmit, reset, setValue, watch } =
    useForm<AccountCreateEditFormFields>();
  const { create: createAccount } = useAccounts("AccountCreateEditModal");
  const { update: updateAccount } = useAccountMutations(account?.id, "AccountCreateEditModal");
  const { saveHandlers } = useDefaultCRUDHandlers("Account");
  const { addNotification } = useNotification();
  const [primaryContact, setPrimaryContact] = useState<IContact>();

  const accountStatusOpts = useMemo(
    () => Object.values(AccountStatus).filter((ct) => isNaN(Number(ct))),
    []
  );

  const onSubmit = (data: AccountCreateEditFormFields) => {
    console.log(data);

    if (data.regularsAccount) {
      data.accountType = AccountType.Manual;
    } else {
      data.accountType = AccountType.Auto;
    }

    if (account) {
      updateAccount.mutate(
        {
          ...account,
          ...data,
          creditLimit:
            data.creditLimit !== "" ? parseFloat(data.creditLimit) : 0,
        },
        {
          ...saveHandlers,
          onSuccess: () => {
            saveHandlers.onSuccess();
            setOpen(false);
          },
        }
      );
    } else {
      if (!primaryContact) {
        addNotification({
          variant: "warn",
          primaryText: "Please select a primary contact",
        });
        return;
      }

      if (data.name === "") {
        addNotification({
          variant: "warn",
          primaryText: "Please enter an account name",
        });
        return;
      }

      createAccount.mutate(
        {
          ...data,
          primaryContactId: primaryContact?.id,
          creditLimit:
            data.creditLimit !== "" ? parseFloat(data.creditLimit) : 0,
        },
        {
          ...saveHandlers,
          onSuccess: () => {
            saveHandlers.onSuccess();
            setOpen(false);
          },
        }
      );
    }
  };

  useEffect(() => {
    if (account) {
      reset({ ...account, creditLimit: String(account.creditLimit), regularsAccount: account.accountType === AccountType.Manual });
    } else {
      reset({
        name: newAccountName,
        status: 2,
      });
    }
  }, [account, open, reset]);

  return (
    <Modal width="max-w-4xl" open={open} setOpen={setOpen}>
      <h2 className="text-xl font-semibold mb-6">{`${account ? "Edit" : "Add"
        } Account`}</h2>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <TextInput label="Account Name" register={register} registerName="name" />
        <SelectInput
          label="Status"
          register={register}
          registerName="status"
          setValue={setValue}
          watch={watch}
          options={accountStatusOpts.map((opt, i) => ({ id: i, name: opt }))}
        />
        <TextInput
          label="VAT Number"
          register={register}
          registerName="vatNumber"
        />
        <TextInput
          label="Credit Limit"
          register={register}
          registerName="creditLimit"
        />
        <div className="hidden md:block md:col-span-2" />
        <TextInput
          label="Bank Account Name"
          register={register}
          registerName="bankAccountName"
        />
        <TextInput
          label="Sort Code"
          register={register}
          registerName="bankSortCode"
        />
        <TextInput
          label="Account Number"
          register={register}
          registerName="bankAccountNumber"
        />
        <CheckboxInput
          label="Regulars Account"
          register={register}
          registerName="regularsAccount"
        />
        {!account && <ContactCombobox onContactSelect={setPrimaryContact} label="Search Contact" />}
        <div className="col-span-1 md:col-span-3">
          <ButtonInput
            label="Save"
            isSubmit={true}
            classes="mt-8"
            onClick={undefined}
          />
        </div>
      </form>
    </Modal>
  );
}
